import { STANDARD_PAGE_RUNTIME } from "playground/apps/manifest";
/**
 * React-side registry for persistent {@link AppHost}s.
 *
 * One provider per workspace holds an `appName → AppHost` map. Hosts are created
 * lazily on first `getOrCreateHost(name)` (an open, or the agent's first tool call)
 * and **stay alive across route changes** — the provider renders every alive
 * host's iframe into a fixed container parked off-screen, and a route declares
 * itself the visible "stage" via {@link useAppStage}, whose rect is mirrored
 * onto the parked container so the iframe fills the route without ever being
 * reparented (which would reload it). A host is torn down only by an explicit
 * {@link RegistryContextValue.closeApp} — that's the App lifecycle: background
 * until closed.
 */
import {
  Component,
  createContext,
  Suspense,
  use,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from "react";

import {
  createAppHost,
  type AppHost,
  type CreateAppHostOptions,
} from "./app-host";

/** Workspace-level config the registry threads into every host it constructs. */
export type RegistryHostConfig = Omit<CreateAppHostOptions, "id">;

interface RegistryContextValue {
  /**
   * Get-or-create the host for `appName` (lazy launch). `initialView` is the
   * rendered entrypoint the iframe loads first (e.g. `"page"` or
   * `"hearth:pinboard"`); it's only honored when the host is first created.
   */
  getOrCreateHost(appName: string, initialView?: string): AppHost;
  /** Whether a host already exists for `appName` (without creating one). */
  hasHost(appName: string): boolean;
  /** Dispose + remove the host for `appName` (the only teardown path). */
  closeApp(appName: string): void;
  /** Set the bounding-rect target for the named host. `null` parks it. */
  setStage(appName: string, rect: DOMRectInit | null): void;
  /**
   * Swap which rendered entrypoint the host's single iframe shows (e.g. `"page"`
   * ⇄ `"hearth:pinboard"`) in place — no reload. No-op if no such host.
   */
  setView(appName: string, view: string): void;
}

const RegistryContext = createContext<RegistryContextValue | null>(null);

export interface AppHostRegistryProviderProps {
  config: RegistryHostConfig;
  children: ReactNode;
}

export function AppHostRegistryProvider({
  config,
  children,
}: AppHostRegistryProviderProps) {
  // Capture config in a ref so workspace re-renders don't churn host creation.
  const configRef = useRef(config);
  configRef.current = config;

  const [hosts, setHosts] = useState<Map<string, AppHost>>(() => new Map());
  const [stages, setStages] = useState<Map<string, DOMRectInit>>(
    () => new Map(),
  );
  // Authoritative map (survives StrictMode double-invocation); the state map is
  // the render-driving copy.
  const cacheRef = useRef<Map<string, AppHost>>(new Map());

  const getOrCreateHost = useCallback(
    (appName: string, initialView: string = STANDARD_PAGE_RUNTIME): AppHost => {
      const cached = cacheRef.current.get(appName);
      if (cached) return cached;
      const created = createAppHost({
        id: appName,
        initialView,
        ...configRef.current,
      });
      cacheRef.current.set(appName, created);
      setHosts((prev) => {
        if (prev.has(appName)) return prev;
        const next = new Map(prev);
        next.set(appName, created);
        return next;
      });
      return created;
    },
    [],
  );

  const hasHost = useCallback(
    (appName: string) => cacheRef.current.has(appName),
    [],
  );

  const closeApp = useCallback((appName: string) => {
    const existing = cacheRef.current.get(appName);
    if (!existing) return;
    existing.dispose();
    cacheRef.current.delete(appName);
    setHosts((prev) => {
      if (!prev.has(appName)) return prev;
      const next = new Map(prev);
      next.delete(appName);
      return next;
    });
    setStages((prev) => {
      if (!prev.has(appName)) return prev;
      const next = new Map(prev);
      next.delete(appName);
      return next;
    });
  }, []);

  const setStage = useCallback((appName: string, rect: DOMRectInit | null) => {
    setStages((prev) => {
      const next = new Map(prev);
      if (rect === null) next.delete(appName);
      else next.set(appName, rect);
      return next;
    });
  }, []);

  // Swap the host's entrypoint in place (no reload, no React re-render needed —
  // the host posts a `render_program` message to its live iframe).
  const setView = useCallback((appName: string, view: string) => {
    cacheRef.current.get(appName)?.setView(view);
  }, []);

  // Tear down every alive host on provider unmount.
  useEffect(() => {
    const cache = cacheRef.current;
    return () => {
      for (const h of cache.values()) h.dispose();
      cache.clear();
    };
  }, []);

  const value = useMemo(
    () => ({ getOrCreateHost, hasHost, closeApp, setStage, setView }),
    [getOrCreateHost, hasHost, closeApp, setStage, setView],
  );

  return (
    <RegistryContext.Provider value={value}>
      {children}
      {[...hosts.entries()].map(([id, host]) => {
        const rect = stages.get(id);
        const visible = rect !== undefined;
        const style: React.CSSProperties = visible
          ? {
              position: "fixed",
              left: rect!.x ?? 0,
              top: rect!.y ?? 0,
              width: rect!.width ?? 0,
              height: rect!.height ?? 0,
              zIndex: 5,
            }
          : {
              position: "fixed",
              left: -9999,
              top: 0,
              width: 1,
              height: 1,
              pointerEvents: "none",
              visibility: "hidden",
            };
        return (
          <div key={id} style={style} data-app-host-stage={id}>
            <FrameErrorBoundary>
              <Suspense fallback={<FrameLoading />}>
                <AppHostFrame host={host} />
              </Suspense>
            </FrameErrorBoundary>
          </div>
        );
      })}
    </RegistryContext.Provider>
  );
}

/**
 * Owns the iframe DOM node for one host. Suspends on the host's cached launch
 * promise via `use()` — `host.src()` returns the *same* promise across retries
 * (memoized), which is what `use()` needs to settle rather than re-suspend
 * forever. It returns a *new* promise only on a shell rebuild (a raw-HTML view
 * swap) — a one-shot transition that re-suspends here and reloads the iframe
 * with a fresh `src`. The enclosing `<Suspense>` shows the loading state and
 * `<FrameErrorBoundary>` catches a build/publish failure.
 *
 * The page loads from a dedicated, per-app **origin** (`src`, not `srcDoc`), so
 * the iframe gets a real cross-domain origin and can run `allow-same-origin`
 * (stable `localStorage`/IndexedDB/etc.) without ever being same-origin as this
 * host — which is what would let it strip its own sandbox.
 */
function AppHostFrame({ host }: { host: AppHost }) {
  // Re-read `host.src()` whenever the host rebuilds its shell (a raw-HTML view
  // swap, or a relaunch after the App changed). The new promise re-suspends
  // here, and keying the iframe on the rebuild reloads it even when the URL is
  // unchanged.
  const [generation, bumpSrc] = useReducer((n: number) => n + 1, 0);
  useEffect(() => host.subscribeSrc(bumpSrc), [host]);
  const src = use(host.src());
  // Permissions-Policy to delegate to the cross-origin App page (geolocation,
  // camera, …), derived from its declared `capabilities.permissions`. Valid once
  // `src` has settled (both await the same launch spec). Omitted when empty so an
  // App that declares nothing delegates nothing.
  const allow = host.allow() || undefined;
  // The iframe `sandbox` tokens: the base isolation floor plus whatever the App
  // declared in `capabilities.sandbox` (e.g. `allow-popups`). Resolved with
  // `src` (both await the same launch spec); always at least the floor.
  const sandbox = host.sandbox();
  const ref = useRef<HTMLIFrameElement | null>(null);

  // The src is set by render; `createSandboxHost` tolerates an already-loaded
  // iframe, so attaching after render is safe.
  useEffect(() => {
    host.attachIframe(ref.current);
    return () => host.attachIframe(null);
  }, [host, src, generation]);

  return (
    <iframe
      key={generation}
      ref={ref}
      title={host.id}
      // The base floor (`allow-scripts allow-same-origin allow-forms`) plus any
      // extra tokens the App declared via `capabilities.sandbox` (e.g.
      // `allow-popups`). `allow-forms` is in the floor because form submission
      // carries no extra attack surface here (the frame is cross-origin and
      // already has `allow-scripts`, so it can POST via `fetch` regardless), and
      // Apps routinely need native <form> submits. Containment-breaking tokens
      // (top-navigation) can't be requested — see `FORBIDDEN_SANDBOX_TOKENS`.
      sandbox={sandbox}
      allow={allow}
      src={src}
      // A pure passthrough: the App page owns all of its own layout. `block` is
      // load-bearing, not cosmetic — a default `display: inline` iframe sits on
      // the text baseline and reserves a ~4px descender strip below itself,
      // silently making this frame taller than its content (a hairline scrollbar
      // / bottom gap). `forest` is shared UI and can't assume a consumer's CSS
      // reset normalises iframes, so we pin `block` + zero border/margin/padding
      // here. (`bg-background` only paints during load, before the page paints.)
      className="block m-0 h-full w-full border-0 p-0 bg-background"
    />
  );
}

/**
 * Skeleton shown while an App's shell is built/published and its iframe loads
 * (the `<Suspense>` fallback for `use(host.src())`). Plain `animate-pulse` divs
 * rather than the host app's `<Skeleton>` primitive — `forest` is shared UI with
 * no dependency on a consumer's component library — using the shared Tailwind
 * theme tokens so it inherits the host look.
 */
function FrameLoading() {
  return (
    <div className="flex h-full w-full flex-col gap-3 p-4">
      <div className="h-6 w-40 animate-pulse rounded-md bg-muted" />
      <div className="h-4 w-full animate-pulse rounded-md bg-muted" />
      <div className="h-4 w-5/6 animate-pulse rounded-md bg-muted" />
      <div className="min-h-0 w-full flex-1 animate-pulse rounded-md bg-muted" />
    </div>
  );
}

/** Catches a compile/render failure in one host's frame (keeps the rest alive). */
class FrameErrorBoundary extends Component<
  { children: ReactNode },
  { error: string | null }
> {
  constructor(props: { children: ReactNode }) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(err: unknown) {
    return { error: err instanceof Error ? err.message : String(err) };
  }
  override render() {
    if (this.state.error !== null) {
      return (
        <pre className="m-0 h-full w-full overflow-auto whitespace-pre-wrap bg-destructive/10 p-4 text-left text-xs text-destructive">
          {this.state.error}
        </pre>
      );
    }
    return this.props.children;
  }
}

export function useAppHostRegistry(): RegistryContextValue {
  const ctx = useContext(RegistryContext);
  if (!ctx) {
    throw new Error(
      "useAppHostRegistry must be called inside <AppHostRegistryProvider>",
    );
  }
  return ctx;
}

/** Like {@link useAppHostRegistry} but returns `null` when no provider is mounted. */
export function useOptionalAppHostRegistry(): RegistryContextValue | null {
  return useContext(RegistryContext);
}

/**
 * Declare that the given DOM element is where the named host's iframe should be
 * visually shown, and which rendered `view` (entrypoint runtime) this surface
 * wants the iframe to show. The registry mirrors the bounding rect onto the
 * parked container; on unmount the iframe returns off-screen (but stays alive).
 * Declaring a different `view` than is currently shown swaps the entrypoint in
 * place (no reload).
 */
export function useAppStage(
  appName: string,
  ref: React.RefObject<HTMLElement | null>,
  view: string = STANDARD_PAGE_RUNTIME,
) {
  const { setStage, setView } = useAppHostRegistry();

  // Ask the host to show this surface's entrypoint. (Separate from the rect
  // effect: it has no DOM cleanup — the next active surface sets its own view.)
  useEffect(() => {
    setView(appName, view);
  }, [appName, view, setView]);

  useLayoutEffect(() => {
    const node = ref.current;
    if (!node) return;
    // Mirror the stage's viewport rect onto the parked iframe every frame.
    // A ResizeObserver/scroll/resize soup only catches the node's *own* size
    // changes and viewport scroll — it misses the node being *moved* by an
    // ancestor/sibling reflow (web fonts loading, a card above dismissing, an
    // async row populating). Those fire no event, so the mirror would go stale
    // and the iframe would drift off the card (a gap at the bottom, overhang at
    // the top). An rAF poll re-measures through any layout change; we dedupe so
    // an unchanged rect costs only a getBoundingClientRect, not a React update.
    let prev: DOMRectInit | null = null;
    const measure = () => {
      const r = node.getBoundingClientRect();
      if (
        prev === null ||
        r.x !== prev.x ||
        r.y !== prev.y ||
        r.width !== prev.width ||
        r.height !== prev.height
      ) {
        prev = { x: r.x, y: r.y, width: r.width, height: r.height };
        setStage(appName, prev);
      }
    };

    // Only poll while the stage is on (or near) screen. A pinboard can hold many
    // tiles; an off-screen one needs no per-frame layout reads, so gate the loop
    // behind an IntersectionObserver — off-screen tiles cost nothing and the
    // whole board no longer runs one permanent 60Hz rAF per pin. The rootMargin
    // arms the loop just before a tile scrolls into view so it's already pinned
    // when it appears, and IntersectionObserver clips against intervening scroll
    // containers, so a tile scrolled out of the pinboard's own scroller stops.
    let raf = 0; // 0 = not looping (rAF handles are always >= 1)
    const loop = () => {
      measure();
      raf = requestAnimationFrame(loop);
    };
    const start = () => {
      if (raf === 0) {
        measure();
        raf = requestAnimationFrame(loop);
      }
    };
    const stop = () => {
      if (raf !== 0) {
        cancelAnimationFrame(raf);
        raf = 0;
      }
    };

    // Measure synchronously first so an above-the-fold tile lands on-target
    // before the first paint, without waiting for the observer's async callback.
    measure();
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) {
          start();
        } else {
          // Park the iframe when the tile leaves the viewport, don't just stop
          // the loop. The host container is position:fixed, so it escapes the
          // pinboard scroller's overflow clip — left at its last rect it would
          // linger, visible and clickable, over other chrome (a tile clipped by
          // the scroller can still have a rect inside the viewport). Parking
          // hides it; start()'s synchronous measure re-shows it on re-entry. The
          // iframe DOM stays mounted throughout, so parking never reloads it.
          stop();
          setStage(appName, null);
        }
      },
      { rootMargin: "200px" },
    );
    io.observe(node);
    return () => {
      io.disconnect();
      stop();
      setStage(appName, null);
    };
  }, [appName, ref, setStage]);
}
